import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { GoogleMap, Marker, Polyline, InfoWindow, Polygon } from '@react-google-maps/api';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { FaSearch } from 'react-icons/fa';

// Marker paths
const MARKER_PATHS = {
    depotMarker: '/img/map-markers/depot-marker.svg',
    chargerMarker: '/img/map-markers/charger-marker.svg',
    selectedDepotMarker: '/img/map-markers/depot-marker-selected.svg',
    selectedChargerMarker: '/img/map-markers/charger-marker-selected.svg',
    markerShadow: '/img/map-markers/marker-shadow.png'
};

// Info Window Content
const landParcelColumns = {
    "legallotid": "Legal ID",
    "name": "Name",
    "lottype": "Lot Type",
    "assessment": "Assessment",
    "siteaddres": "Site Address",
    "assdamt": "Assessed Amount",
    "landval": "Land Value",
    "improvedval": "Improved Value",
    "sitezip5": "Zip Code",
    "sitecityst": "City/State",
    "shape_area": "Area",
}

const mhdInfrastructureColumns = {
    "name": "Name",
    "streetaddress": "Street Address",
    "city": "City",
    "state": "State",
    "zipcode": "Zip Code",
    "devstatus": "Status",
    "access_type": "Access Type",
    "stall_count": "Stall Count",
    "plug_count": "Plug Count",
}

const warehouseColumns = {
    "place_name": "Name",
    "apn": "APN",
    "shape_area": "Area",
    "category": "Category",
    "warehouse_class": "Warehouse Class",
    "warehouse_type": "Warehouse Type",
    "county": "County",
}

const InfoWindowPopup = ({
    selectedContent,
    type,
}) => {
    if (!selectedContent) return null;
    
    const formatValue = (key, value) => {
        if (value === null || value === undefined || value === '') return 'N/A';
        
        // Format specific fields
        if (key === 'landval' || key === 'improvedval' || key === 'assdamt') {
            return `$${parseInt(value || 0).toLocaleString()}`;
        }
        if (key === 'shape_area') {
            return `${Math.round(value || 0).toLocaleString()} sq ft`;
        }
        if (key === 'zipcode') {
            return Math.trunc(value) || 'N/A';
        }
        
        return value;
    };
    
    if (type === "landParcel") {
        return (
            <>
                {Object.keys(landParcelColumns).map((column) => {
                    const value = formatValue(column, selectedContent[column]);
                    return (
                        <div key={column}>
                            <span className="font-semibold">{landParcelColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }
    else if (type === "mhdInfrastructure") {
        return (
            <>
                {Object.keys(mhdInfrastructureColumns).map((column) => {
                    const value = formatValue(column, selectedContent[column]);
                    return (
                        <div key={column}>
                            <span className="font-semibold">{mhdInfrastructureColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }
    else if (type === "warehouse") {
        return (
            <>
                {Object.keys(warehouseColumns).map((column) => {
                    const value = formatValue(column, selectedContent[column]);
                    return (
                        <div key={column}>
                            <span className="font-semibold">{warehouseColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }
    
    // Default case - return null if type doesn't match
    return null;
}

// Function to parse WKT polygon geometry to Google Maps format
const parseWKTPolygon = (wktString) => {
    try {
        // Remove SRID prefix and extract polygon coordinates
        // Handle both "SRID=4326;POLYGON" and "POLYGON" formats
        const cleanWkt = wktString.replace(/^SRID=\d+;/, '');
        const polygonMatch = cleanWkt.match(/POLYGON\s*\(\(([^)]+)\)\)/i);
        if (!polygonMatch) return null;
        
        const coordinateString = polygonMatch[1];
        const coordinates = coordinateString.split(',').map(coord => {
            const [x, y] = coord.trim().split(' ').map(Number);
            
            // Check if coordinates are in projected format (Web Mercator)
            // Web Mercator coordinates are typically very large numbers
            if (Math.abs(x) > 180 || Math.abs(y) > 90) {
                // These are projected coordinates, need to convert to lat/lng
                // Web Mercator (EPSG:3857) to WGS84 (EPSG:4326) conversion
                const lng = (x / 20037508.34) * 180;
                const lat = (Math.atan(Math.exp((y / 20037508.34) * Math.PI)) * 360 / Math.PI) - 90;
                return { lat, lng };
            } else {
                // Already in lat/lng format
                return { lat: y, lng: x };
            }
        });
        
        return coordinates;
    } catch (error) {
        console.error('Error parsing WKT polygon:', error);
        return null;
    }
};

// Memoized polygon component for better performance
const MemoizedPolygon = React.memo(({ parcel, onClick, zoomLevel }) => {
    const path = parseWKTPolygon(parcel.geometry);
    if (!path) return null;

    // Dynamic styling based on zoom level and parcel size
    const getPolygonStyle = () => {
        const baseOpacity = zoomLevel > 12 ? 0.3 : 0.2;
        const strokeWeight = zoomLevel > 14 ? 2 : 1;
        
        // Color coding based on land value
        const landValue = parseInt(parcel.landval || 0);
        let fillColor = '#4F46E5'; // Default blue
        
        if (landValue > 1000000) {
            fillColor = '#DC2626'; // Red for high value
        } else if (landValue > 500000) {
            fillColor = '#F59E0B'; // Orange for medium value
        } else if (landValue > 100000) {
            fillColor = '#10B981'; // Green for moderate value
        }
        
        return {
            fillColor: fillColor,
            fillOpacity: baseOpacity,
            strokeColor: fillColor,
            strokeOpacity: 0.8,
            strokeWeight: strokeWeight,
            clickable: true,
            draggable: false,
            editable: false,
            geodesic: false,
            zIndex: Math.floor(landValue / 10000) // Higher value parcels on top
        };
    };

    return (
        <Polygon
            key={parcel.id}
            path={path}
            onClick={onClick}
            options={getPolygonStyle()}
        />
    );
});

const MemoizedSCAQMDJurisdiction = React.memo(({ jurisdiction, zoomLevel, onClick }) => {
    console.log("MemoizedSCAQMDJurisdiction rendering:", jurisdiction);
    console.log("Jurisdiction geometry:", jurisdiction.geometry);
    
    const path = parseWKTPolygon(jurisdiction.geometry);
    console.log("Parsed path:", path);
    
    if (path && path.length > 0) {
        console.log("First coordinate:", path[0], "Last coordinate:", path[path.length - 1]);
        console.log("Coordinate range - Lat:", Math.min(...path.map(p => p.lat)), "to", Math.max(...path.map(p => p.lat)));
        console.log("Coordinate range - Lng:", Math.min(...path.map(p => p.lng)), "to", Math.max(...path.map(p => p.lng)));
    }
    
    if (!path) {
        console.log("No path parsed for jurisdiction:", jurisdiction.id);
        return null;
    }

    console.log("Rendering SCAQMD polygon with path length:", path.length);
    return (
        <Polygon
            key={jurisdiction.id}
            path={path}
            onClick={onClick}
            options={{
                fillColor: '#9333EA',
                fillOpacity: 0.3,
                strokeColor: '#9333EA',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: true,
                draggable: false,
                editable: false,
                geodesic: false,
                zIndex: 100 // Higher than parcels so it shows on top
            }}
        />
    );
});

const MemoizedWarehouse = React.memo(({ warehouse, zoomLevel, onClick }) => {
    console.log("MemoizedWarehouse rendering:", warehouse);
    console.log("Warehouse geometry:", warehouse.geometry);
    
    const path = parseWKTPolygon(warehouse.geometry);
    console.log("Parsed path:", path);

    return (
        <Polygon
            key={warehouse.id}
            path={path}
            onClick={(event) => onClick(warehouse, event)}
            options={{
                fillColor: '#059669',
                fillOpacity: 0.3,
                strokeColor: '#059669',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: true,
                draggable: false,
                editable: false,
                geodesic: false,
                zIndex: 100 // Higher than parcels so it shows on top
            }}
        />
    );
});

// ==== Marker Icons ====
const createMarkerIcons = () => {
    if (!window.google) return null;

    return {
        charger: {
            default: {
                url: MARKER_PATHS.chargerMarker,
                scaledSize: new window.google.maps.Size(30, 49),
                anchor: new window.google.maps.Point(15, 38),
                labelOrigin: new window.google.maps.Point(15, -10),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(49, 49),
                    anchor: new window.google.maps.Point(15, 49)
                }
            },
            selected: {
                url: MARKER_PATHS.selectedChargerMarker,
                scaledSize: new window.google.maps.Size(30, 49),
                anchor: new window.google.maps.Point(15, 38),
                labelOrigin: new window.google.maps.Point(15, -10),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(49, 49),
                    anchor: new window.google.maps.Point(15, 49)
                }
            }
        },
        depot: {
            default: {
                url: MARKER_PATHS.depotMarker,
                scaledSize: new window.google.maps.Size(45, 73),
                anchor: new window.google.maps.Point(22, 58),
                labelOrigin: new window.google.maps.Point(22, -15),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(73, 73),
                    anchor: new window.google.maps.Point(22, 73)
                }
            },
            selected: {
                url: MARKER_PATHS.selectedDepotMarker,
                scaledSize: new window.google.maps.Size(45, 73),
                anchor: new window.google.maps.Point(22, 58),
                labelOrigin: new window.google.maps.Point(22, -15),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(73, 73),
                    anchor: new window.google.maps.Point(22, 73)
                }
            }
        }
    };
};

const MapControls = ({
    landParcelCheckbox, setLandParcelCheckbox,
    SCAQMDCheckbox, setSCAQMDCheckbox,
    mhdInfrastructureCheckbox, setMhdInfrastructureCheckbox,
    warehouseCheckbox, setWarehouseCheckbox,
    truckStopCheckbox, setTruckStopCheckbox,
    truckParkingLocationCheckbox, setTruckParkingLocationCheckbox,
    pemsTrafficCheckbox, setPemsTrafficCheckbox,
    epaCEJSTCheckbox, setEPACEJSTCheckbox
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    return (
        <div className="absolute bottom-4 left-4 z-10 bg-white rounded-lg shadow-lg border border-gray-200 max-w-xs flex flex-col">
            {/* Header with title - gets pushed up when expanded */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Map Features</h3>
                <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    aria-label={isCollapsed ? "Expand legend" : "Collapse legend"}
                >
                    <svg 
                        className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            </div>
            
            {/* Collapsible content - appears below header */}
            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                isCollapsed ? 'max-h-0' : 'max-h-96'
            }`}>
                <div className="p-2 space-y-1">
                    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={landParcelCheckbox}
                            onChange={(e) => setLandParcelCheckbox(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">Land Parcels</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={SCAQMDCheckbox}
                            onChange={(e) => setSCAQMDCheckbox(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">SCAQMD Jurisdiction</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={mhdInfrastructureCheckbox}
                            onChange={(e) => setMhdInfrastructureCheckbox(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">Existing Chargers</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={warehouseCheckbox}
                            onChange={(e) => setWarehouseCheckbox(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">Warehouses</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={truckStopCheckbox}
                            onChange={(e) => setTruckStopCheckbox(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">Truck Stops</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={truckParkingLocationCheckbox}
                            onChange={(e) => setTruckParkingLocationCheckbox(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">Truck Parking</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={pemsTrafficCheckbox}
                            onChange={(e) => setPemsTrafficCheckbox(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">Traffic</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={epaCEJSTCheckbox}
                            onChange={(e) => setEPACEJSTCheckbox(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">Disadvantaged Communities</span>
                    </label>
                </div>
            </div>
        </div>
    )
}

export const ChargerGoogleMap = forwardRef(({ 
    landParcelData = [],
    scaqmdJurisdiction = [],
    mhdInfrastructure = [],
    warehouses = [],
    needManualSearch,
    handleSearchInArea,
    onBoundsChange
}, ref) => {
    const { isLoaded, loadError } = useGoogleMaps();
    const [markerIcons, setMarkerIcons] = useState(null);
    const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
    const [selectedParcel, setSelectedParcel] = useState(null);
    const [infoWindowPosition, setInfoWindowPosition] = useState(null);
    const [selectedContentType, setSelectedContentType] = useState(null);
    const mapRef = useRef(null);
    const boundsChangeTimeoutRef = useRef(null);
    const [currentZoom, setCurrentZoom] = useState(10);
    const isInitialLoadRef = useRef(true);

    const [landParcelCheckbox, setLandParcelCheckbox] = useState(true);
    const [SCAQMDCheckbox, setSCAQMDCheckbox] = useState(false);
    const [mhdInfrastructureCheckbox, setMhdInfrastructureCheckbox] = useState(false);
    const [warehouseCheckbox, setWarehouseCheckbox] = useState(false);
    const [truckStopCheckbox, setTruckStopCheckbox] = useState(false);
    const [truckParkingLocationCheckbox, setTruckParkingLocationCheckbox] = useState(false);
    const [pemsTrafficCheckbox, setPemsTrafficCheckbox] = useState(false);
    const [epaCEJSTCheckbox, setEPACEJSTCheckbox] = useState(false);

    // Expose map methods via ref
    useImperativeHandle(ref, () => ({
        getMap: () => mapRef.current,
        getBounds: () => {
            if (mapRef.current) {
                const bounds = mapRef.current.getBounds();
                if (bounds) {
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();
                    return {
                        north: ne.lat(),
                        south: sw.lat(),
                        east: ne.lng(),
                        west: sw.lng()
                    };
                }
            }
            return null;
        }
    }));

    const onLoad = useCallback((map) => {
        console.log('Map loaded successfully');
        mapRef.current = map;
        setIsGoogleMapsLoaded(true);
        const icons = createMarkerIcons();
        setMarkerIcons(icons);
        
        // Set initial position only once
        if (isInitialLoadRef.current) {
            map.setCenter({ lat: 33.9, lng: -117.5 });
            map.setZoom(10);
            isInitialLoadRef.current = false;
        }
        
        // Initial bounds change after a short delay to ensure map is fully loaded
        setTimeout(() => {
            if (onBoundsChange) {
                const bounds = map.getBounds();
                const zoom = map.getZoom();
                setCurrentZoom(zoom);
                
                if (bounds) {
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();
                    const boundsObj = {
                        north: ne.lat(),
                        south: sw.lat(),
                        east: ne.lng(),
                        west: sw.lng()
                    };
                    console.log('Initial bounds:', boundsObj, 'Zoom:', zoom);
                    onBoundsChange(boundsObj, zoom);
                }
            }
        }, 100);
    }, [onBoundsChange]);

    // Debounced bounds change handler
    const handleBoundsChanged = useCallback(() => {
        if (!mapRef.current || !onBoundsChange) return;
        
        if (boundsChangeTimeoutRef.current) {
            clearTimeout(boundsChangeTimeoutRef.current);
        }
        
        boundsChangeTimeoutRef.current = setTimeout(() => {
            const bounds = mapRef.current.getBounds();
            const zoom = mapRef.current.getZoom();
            setCurrentZoom(zoom);
            
            if (bounds) {
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                const boundsObj = {
                    north: ne.lat(),
                    south: sw.lat(),
                    east: ne.lng(),
                    west: sw.lng()
                };
                console.log('Bounds changed:', boundsObj, 'Zoom:', zoom);
                onBoundsChange(boundsObj, zoom);
            }
        }, 300); // 300ms debounce
    }, [onBoundsChange]);

    const onError = (error) => {
        console.error('Google Maps failed to load:', error);
    };

    // Handle polygon click
    const handlePolygonClick = useCallback((parcel, event) => {
        setSelectedParcel(parcel);
        setInfoWindowPosition({
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
        });
        // Determine content type based on the parcel data structure
        if (parcel.place_name || parcel.warehouse_class) {
            setSelectedContentType('warehouse');
        } else {
            setSelectedContentType('landParcel');
        }
    }, []);

    // Handle MHD infrastructure marker click
    const handleMhdMarkerClick = useCallback((infrastructure, event) => {
        setSelectedParcel(infrastructure);
        setInfoWindowPosition({
            lat: parseFloat(infrastructure.lat),
            lng: parseFloat(infrastructure.lng)
        });
        setSelectedContentType('mhdInfrastructure');
    }, []);

    // Close info window
    const closeInfoWindow = useCallback(() => {
        setSelectedParcel(null);
        setInfoWindowPosition(null);
        setSelectedContentType(null);
    }, []);

    // Memoize parcel rendering for performance
    const renderedParcels = React.useMemo(() => {
        if (!landParcelCheckbox || !landParcelData.length) return null;
        
        return landParcelData.map((parcel) => (
            <MemoizedPolygon
                key={parcel.id}
                parcel={parcel}
                zoomLevel={currentZoom}
                onClick={(event) => handlePolygonClick(parcel, event)}
            />
        ));
    }, [landParcelCheckbox, landParcelData, currentZoom, handlePolygonClick]);

    const renderedSCAQMDJurisdiction = React.useMemo(() => {
        console.log("SCAQMD Jurisdiction data:", scaqmdJurisdiction);
        console.log("SCAQMD Checkbox checked:", SCAQMDCheckbox);
        if (!SCAQMDCheckbox || !scaqmdJurisdiction.length) {
            console.log("Not rendering SCAQMD - checkbox:", SCAQMDCheckbox, "data length:", scaqmdJurisdiction.length);
            return null;
        }
        console.log("Rendering SCAQMD Jurisdiction with", scaqmdJurisdiction.length, "jurisdictions");
        
        return scaqmdJurisdiction.map((jurisdiction, index) => {
            console.log(`Rendering jurisdiction ${index}:`, jurisdiction);
            return (
                <MemoizedSCAQMDJurisdiction
                    key={jurisdiction.id || index}
                    jurisdiction={jurisdiction}
                    zoomLevel={currentZoom}
                    onClick={(event) => {
                        console.log("SCAQMD jurisdiction clicked:", jurisdiction);
                    }}
                />
            );
        });
    }, [SCAQMDCheckbox, scaqmdJurisdiction, currentZoom]);

    // Memoize MHD infrastructure markers for performance
    const renderedMhdInfrastructure = React.useMemo(() => {
        if (!mhdInfrastructureCheckbox || !mhdInfrastructure.length) return null;
        
        console.log("Rendering MHD Infrastructure with", mhdInfrastructure.length, "locations");
        
        return mhdInfrastructure.map((infrastructure, index) => {
            // Ensure we have valid lat/lng coordinates
            const lat = parseFloat(infrastructure.lat);
            const lng = parseFloat(infrastructure.lng);
            
            if (isNaN(lat) || isNaN(lng)) {
                console.warn(`Invalid coordinates for MHD infrastructure ${index}:`, infrastructure);
                return null;
            }
            
            return (
                <Marker
                    key={infrastructure.id || index}
                    position={{ lat, lng }}
                    onClick={(event) => handleMhdMarkerClick(infrastructure, event)}
                    icon={{
                        url: 'data:image/svg+xml;base64,' + btoa(`
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" fill="#059669" stroke="#065f46" stroke-width="2"/>
                                <circle cx="12" cy="12" r="4" fill="white"/>
                            </svg>
                        `),
                        scaledSize: new window.google.maps.Size(24, 24),
                        anchor: new window.google.maps.Point(12, 12)
                    }}
                    title={infrastructure.name || `MHD Infrastructure ${index + 1}`}
                />
            );
        });
    }, [mhdInfrastructureCheckbox, mhdInfrastructure, handleMhdMarkerClick]);

    const renderedWarehouses = React.useMemo(() => {
        if (!warehouseCheckbox || !warehouses.length) return null;
        
        return warehouses.map((warehouse, index) => {
            return <MemoizedWarehouse key={warehouse.id || index} warehouse={warehouse} zoomLevel={currentZoom} onClick={(warehouse, event) => handlePolygonClick(warehouse, event)} />
        });
    }, [warehouseCheckbox, warehouses, currentZoom, handlePolygonClick]);

    if (loadError) {
        console.error('Google Maps load error:', loadError);
        return <div className="flex items-center justify-center h-[90vh] text-red-600">Error loading Google Maps: {loadError.message}</div>;
    }

    if (!isLoaded) {
        return <div className="flex items-center justify-center h-[90vh] text-gray-600">Loading Google Maps...</div>;
    }

    console.log('Rendering map with', landParcelData.length, 'parcels');

    return (
        <div className="relative h-[90vh]">
            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                onLoad={onLoad}
                onBoundsChanged={handleBoundsChanged}
                onClick={closeInfoWindow}
                options={{
                    disableDefaultUI: false,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: true,
                    fullscreenControl: true,
                    gestureHandling: 'greedy',
                    styles: [
                        {
                            featureType: "poi",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "poi.business",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "poi.park",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "poi.school",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "poi.medical",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "poi.attraction",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "poi.government",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "poi.sports_complex",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "poi.place_of_worship",
                            stylers: [{ visibility: "off" }]
                        },
                        {
                            featureType: "transit",
                            stylers: [{ visibility: "off" }]
                        }
                    ]
                }}
            >
                {/* Render SCAQMD Jurisdictions first (underneath) */}
                {renderedSCAQMDJurisdiction}

                {/* Render MHD Infrastructure */}
                {renderedMhdInfrastructure}
                
                {/* Render Land Parcels on top */}
                {renderedParcels}

                {/* Render Warehouses */}
                {renderedWarehouses}

                {/* Info Window for selected parcel or infrastructure */}
                {selectedParcel && infoWindowPosition && (
                    <InfoWindow
                        position={infoWindowPosition}
                        onCloseClick={closeInfoWindow}
                    >
                        <div className="p-3 max-w-sm">
                            <h3 className="font-semibold text-gray-800 mb-2">
                                {selectedContentType === 'mhdInfrastructure' ? 'Charger Information' : 
                                 selectedContentType === 'warehouse' ? 'Warehouse Information' : 
                                 'Land Parcel Information'}
                            </h3>
                            <div className="space-y-1 text-sm">
                                <InfoWindowPopup 
                                    selectedContent={selectedParcel}
                                    type={selectedContentType}
                                />
                            </div>
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
            <MapControls
                landParcelCheckbox={landParcelCheckbox}
                setLandParcelCheckbox={setLandParcelCheckbox}
                SCAQMDCheckbox={SCAQMDCheckbox}
                setSCAQMDCheckbox={setSCAQMDCheckbox}
                mhdInfrastructureCheckbox={mhdInfrastructureCheckbox}
                setMhdInfrastructureCheckbox={setMhdInfrastructureCheckbox}
                warehouseCheckbox={warehouseCheckbox}
                setWarehouseCheckbox={setWarehouseCheckbox}
                truckStopCheckbox={truckStopCheckbox}
                setTruckStopCheckbox={setTruckStopCheckbox}
                truckParkingLocationCheckbox={truckParkingLocationCheckbox}
                setTruckParkingLocationCheckbox={setTruckParkingLocationCheckbox}
                pemsTrafficCheckbox={pemsTrafficCheckbox}
                setPemsTrafficCheckbox={setPemsTrafficCheckbox}
                epaCEJSTCheckbox={epaCEJSTCheckbox}
                setEPACEJSTCheckbox={setEPACEJSTCheckbox}
            />
            
            {/* Performance indicator
            <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded text-sm">
                Zoom: {currentZoom} • {landParcelData.length} parcels
            </div> */}
            {/* Manual search button */}
            {needManualSearch && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2">
                <button className="bg-blue-500 text-white px-3 py-1 rounded text-sm" onClick={() => handleSearchInArea()}>
                    <span className="flex items-center gap-2 p-1"><FaSearch size={16}/> Search in this area</span>
                </button>
            </div>
            )}
        </div>
    );
});